import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { trpc } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  Calendar,
  Clock,
  Dumbbell,
  Users,
  Wrench,
  Info,
  ChevronRight,
  X,
  Settings,
} from "lucide-react";
import { useCommunity } from "@/contexts/CommunityContext";
import { toast } from "sonner";

const categoryIcons = {
  SPORT: Dumbbell,
  SOCIAL: Users,
  SERVICE: Wrench,
};

const categoryLabels = {
  SPORT: "Deporte",
  SOCIAL: "Social",
  SERVICE: "Servicio",
};

function formatTime(date: Date | string, timeZone?: string) {
  return new Date(date).toLocaleTimeString("es-AR", {
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
    timeZone,
  });
}

function formatDate(date: Date | string, timeZone?: string) {
  return new Date(date).toLocaleDateString("es-AR", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone,
  });
}

function formatDateTime(date: Date | string, timeZone?: string) {
  return `${formatDate(date, timeZone)} a las ${formatTime(date, timeZone)}`;
}

export function AmenitiesPage() {
  const utils = trpc.useUtils();
  const { selectedCommunityId } = useCommunity();
  const [cancelReservationId, setCancelReservationId] = useState<string | null>(
    null,
  );

  const { data: communities, isLoading: communitiesLoading } =
    trpc.amenity.getUserCommunities.useQuery();

  const tz = communities?.find((c) => c.id === selectedCommunityId)?.timezone;

  // Check if user has admin access to manage amenities for the selected community
  const { data: adminCommunities } =
    trpc.amenity.getAdminCommunities.useQuery();
  const hasAdminAccess =
    selectedCommunityId &&
    adminCommunities?.some((c) => c.id === selectedCommunityId);

  const { data: amenities, isLoading: amenitiesLoading } =
    trpc.amenity.getAmenities.useQuery(
      { communityId: selectedCommunityId || "" },
      { enabled: !!selectedCommunityId },
    );

  const cancelReservation = trpc.amenity.cancelReservation.useMutation({
    onSuccess: () => {
      utils.amenity.getAmenities.invalidate();
      toast.success("Reserva cancelada");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  // No communities - show message
  if (!communitiesLoading && (!communities || communities.length === 0)) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold md:text-3xl">Amenities</h1>
          <p className="text-muted-foreground">
            Explorá y reservá amenities de tu barrio.
          </p>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            No sos miembro de ningún barrio. Contactá al administrador para
            obtener acceso.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold md:text-3xl">Amenities</h1>
          <p className="text-muted-foreground">
            Explorá y reservá amenities de tu barrio.
          </p>
        </div>

        {hasAdminAccess && (
          <Link
            to="/amenities/manage"
            aria-label="Gestionar amenities"
            className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <Settings className="h-5 w-5" />
          </Link>
        )}
      </div>

      {communitiesLoading || amenitiesLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : amenities && amenities.length > 0 ? (
        <div className="space-y-4">
          {amenities.map((amenity) => {
            const CategoryIcon = categoryIcons[amenity.category];

            return (
              <Card key={amenity.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
                        <CategoryIcon className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">
                          {amenity.name}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {categoryLabels[amenity.category]}
                          </Badge>
                          {amenity.requiresApproval && (
                            <Badge variant="outline" className="text-xs">
                              Requiere aprobación
                            </Badge>
                          )}
                        </CardDescription>
                      </div>
                    </div>

                    <Link
                      to="/amenities/$amenityId"
                      params={{ amenityId: amenity.id }}
                    >
                      <Button variant="outline" size="sm">
                        Reservar
                        <ChevronRight className="ml-1 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  {/* Next available slot */}
                  {amenity.nextAvailableSlot && (
                    <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      <span>
                        Próximo disponible:{" "}
                        <span className="font-medium text-foreground">
                          {formatDateTime(
                            amenity.nextAvailableSlot.startTime,
                            tz,
                          )}
                        </span>
                      </span>
                    </div>
                  )}

                  {/* User's reservations */}
                  {amenity.myReservations.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Tus reservas:</p>
                      <div className="space-y-2">
                        {amenity.myReservations.map((reservation) => (
                          <div
                            key={reservation.id}
                            className="flex items-center justify-between rounded-xl bg-muted/50 px-3 py-2"
                          >
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">
                                {formatDate(reservation.startTime, tz)},{" "}
                                {formatTime(reservation.startTime, tz)} -{" "}
                                {formatTime(reservation.endTime, tz)}
                              </span>
                              <Badge
                                variant={
                                  reservation.status === "CONFIRMED"
                                    ? "default"
                                    : reservation.status === "PENDING"
                                      ? "secondary"
                                      : "destructive"
                                }
                                className="text-xs"
                              >
                                {reservation.status.toLowerCase()}
                              </Badge>
                            </div>

                            {(reservation.status === "CONFIRMED" ||
                              reservation.status === "PENDING") && (
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label="Cancelar reserva"
                                onClick={() =>
                                  setCancelReservationId(reservation.id)
                                }
                                disabled={
                                  cancelReservation.isPending &&
                                  cancelReservation.variables?.reservationId ===
                                    reservation.id
                                }
                              >
                                {cancelReservation.isPending &&
                                cancelReservation.variables?.reservationId ===
                                  reservation.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <X className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="mx-auto h-12 w-12 text-muted-foreground" />
            <p className="mt-4 text-muted-foreground">
              No hay amenities disponibles en este barrio.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Cancel Reservation Confirmation */}
      <AlertDialog
        open={!!cancelReservationId}
        onOpenChange={(open) => {
          if (!open) setCancelReservationId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar reserva?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Tu turno quedará disponible para
              otros residentes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Volver</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (cancelReservationId) {
                  cancelReservation.mutate({
                    reservationId: cancelReservationId,
                  });
                  setCancelReservationId(null);
                }
              }}
            >
              Cancelar reserva
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
